import React, { useState, useRef } from "react";
import {
  makeStyles,
  Typography,
  ClickAwayListener,
  Popper,
  Paper,
  MenuList,
  MenuItem,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

const useStyles = makeStyles(() => ({
  formControl: {
    position: "relative",
    backgroundColor: "#F1F1F1",
    borderRadius: "4px",
    height: "2.4rem",
    cursor: "pointer",
    padding: "0.1rem 0.6rem 0.1rem 0.8rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
    // Ensure proper touch handling on mobile
    touchAction: "manipulation",
    userSelect: "none",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    // Add minimum touch target size for mobile accessibility
    minHeight: "44px",
    '@media (max-width: 768px)': {
      minHeight: "48px", // Even larger touch target on mobile
      padding: "0.2rem 0.6rem 0.2rem 0.8rem",
    },
  },
  iconWrapper: {
    width: "2rem",
    height: "2rem",
    color: "#000",
    transition: "transform 0.3s ease",
  },
  popper: {
    zIndex: 9997,
  },
  paper: {
    backgroundColor: "#F1F1F1",
    borderRadius: "6px",
    boxShadow: "none",
    padding: 0,
  },
  menuContainer: {
    padding: 0,
    maxHeight: "16rem",
    overflowY: "auto",
    // Custom scrollbar styling
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#E0E0E0",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#9f9f9f",
      borderRadius: "4px",
      border: "1px solid #E0E0E0",
      "&:hover": {
        backgroundColor: "#7f7f7f",
      },
    },
    "&::-webkit-scrollbar-corner": {
      backgroundColor: "#E0E0E0",
    },
    // Firefox scrollbar styling
    scrollbarWidth: "thin",
    scrollbarColor: "#9f9f9f #E0E0E0",
  },
  menuItem: {
    color: "#000",
    width: "100%",
    height: "2rem",
    padding: "0.6rem 0.6rem 0.6rem 0.8rem",
    // Ensure proper touch handling
    touchAction: "manipulation",
    userSelect: "none",
    WebkitTouchCallout: "none",
    WebkitUserSelect: "none",
    // Minimum touch target size for mobile
    minHeight: "44px",
    '@media (max-width: 768px)': {
      minHeight: "48px",
      padding: "0.8rem 0.6rem 0.8rem 0.8rem",
    },
    "&.Mui-selected": {
      color: "#ffffff",
      backgroundColor: "#FF7500",
    },
    "&.Mui-selected:hover": {
      backgroundColor: "#FF7500",
    },
    "&:first-child.Mui-selected": {
      borderTopLeftRadius: "6px",
      borderTopRightRadius: "6px",
    },
    "&:last-child.Mui-selected": {
      borderBottomLeftRadius: "6px",
      borderBottomRightRadius: "6px",
    },
    "&:first-child:hover": {
      borderTopLeftRadius: "6px",
      borderTopRightRadius: "6px",
    },
    "&:last-child:hover": {
      borderBottomLeftRadius: "6px",
      borderBottomRightRadius: "6px",
    },
  },
  selected: {
    color: "#000",
  },
}));

export default function SelectItemWizard({ value, setOption, defaultOption, selectValues }) {
  const classes = useStyles();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleToggle = () => setOpen((prevOpen) => !prevOpen);

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) return;
    setOpen(false);
  };

  const handleSelect = (option) => {
    setOption(option); 
    setOpen(false);
  };

  const displayLabel = value?.trim() === '' ? defaultOption : value;

  return (
    <>
      <ClickAwayListener onClickAway={handleClose}>
        <div 
          ref={anchorRef} 
          className={classes.formControl} 
          onClick={handleToggle}
          role="button"
          tabIndex={0}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <Typography className={classes.selected}>{displayLabel}</Typography>
          <ArrowDropDownIcon
            className={classes.iconWrapper}
            style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          />
        </div>
      </ClickAwayListener>

      <Popper open={open} anchorEl={anchorRef.current} className={classes.popper} placement="bottom-start" disablePortal>
        <Paper className={classes.paper} style={{ width: anchorRef.current?.offsetWidth ?? "auto" }}>
          <MenuList className={classes.menuContainer}>
            {selectValues.map((option) => (
              <MenuItem
                key={option}
                selected={option === value}
                onClick={() => handleSelect(option)}
                className={classes.menuItem}
              >
                <Typography>{option}</Typography>
              </MenuItem>
            ))}
          </MenuList>
        </Paper>
      </Popper>
    </>
  );
}